import { DxContainer, DxIcon, DxLink, DxText } from '@dvag/design-system-react';
import i18next from 'i18next';
import { useState } from 'react';
import { CustomerResultAddress } from 'type/address';
import { CustomerStatus } from 'type/customerStatus';
import { formatCustomerResultAddress } from 'utils/formatCustomerResultAddress';
import { checkIcon, getPersonFullName } from 'utils/util';
import { CustomerStatusBadge } from '../customerStatus/CustomerStatusBadge';

import './style.css';

export interface CustomerResultPerson {
  firstName: string;
  id: string;
  lastName: string;
  status?: CustomerStatus;
}

interface CustomerResultItemProps {
  additionalPersons?: CustomerResultPerson[];
  address?: CustomerResultAddress;
  onClick: () => void;
  person: CustomerResultPerson;
  testId: string;
}

export const CustomerResultItem = ({
  additionalPersons = [],
  address,
  onClick,
  person,
  testId,
}: CustomerResultItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const addressLabel = formatCustomerResultAddress(address);
  const personName = getPersonFullName(person);
  const visibleAdditionalPersons = isExpanded ? additionalPersons : additionalPersons.slice(0, 1);
  const hiddenPersonsCount = Math.max(additionalPersons.length - 1, 0);
  const itemClassName = `customer-result-item${
    additionalPersons.length ? ' customer-result-item--with-members' : ''
  }${hiddenPersonsCount > 0 && !isExpanded ? ' customer-result-item--with-toggle' : ''}${
    isExpanded ? ' customer-result-item--expanded' : ''
  }`;

  return (
    <DxContainer className={itemClassName} color="white" role="listitem">
      <button
        aria-label={`${personName} ${person.id}`}
        className="customer-result-action"
        data-testid={testId}
        onClick={onClick}
        type="button"
      />

      <div className="customer-result-main">
        <CustomerStatusBadge status={person.status} />
        <div className="customer-result-content">
          <div className="customer-result-title">
            <DxText className="customer-result-name" color="headline" type="Paragraph-Standard">
              {personName}
            </DxText>
            <DxText className="customer-result-number" color="gray-66" type="Infotext-Small">
              {person.id}
            </DxText>
          </div>
          {addressLabel ? (
            <DxText className="customer-result-address" color="gray-66" type="Infotext-Small">
              {addressLabel}
            </DxText>
          ) : null}
        </div>
        <DxIcon
          aria-hidden="true"
          className="customer-result-navigation"
          color="default"
          icon={checkIcon('chevron-rechts')}
          size={16}
        />
      </div>

      {additionalPersons.length ? (
        <div className="customer-result-members">
          <div className="customer-result-member-list">
            {visibleAdditionalPersons.map((additionalPerson) => (
              <div
                className="customer-result-tree-row customer-result-member"
                key={additionalPerson.id}
              >
                <CustomerStatusBadge compact status={additionalPerson.status} />
                <DxText
                  className="customer-result-member-name"
                  color="headline"
                  type="Infotext-Standard"
                >
                  {getPersonFullName(additionalPerson)}
                </DxText>
                <DxText
                  className="customer-result-member-number"
                  color="gray-66"
                  type="Infotext-Small"
                >
                  {additionalPerson.id}
                </DxText>
              </div>
            ))}

            {hiddenPersonsCount > 0 && !isExpanded ? (
              <div className="customer-result-tree-row customer-result-toggle-row">
                <DxLink
                  aria-expanded="false"
                  className="customer-result-toggle"
                  onClick={() => setIsExpanded(true)}
                >
                  {i18next.t('dashboard.showMoreHouseholdMembers', {
                    count: hiddenPersonsCount,
                  })}
                </DxLink>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </DxContainer>
  );
};
